import { useEffect, useRef, useState } from 'react';

// react-snap runs the page long enough for the idle loader below to fire and
// would serialise the injected <script async> into every prerendered page,
// defeating the deferral (the widget then loads at t=0 for every visitor).
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const AGENT_ID = 'agent_5001kpz6eptxfqzb1gmw496m942v';

// Vastgepinde versie: de oude elevenlabs.io/convai-widget bundle negeerde de
// nieuwe widget_config-velden (variant, expandable, text_contents), waardoor de
// widget in het Engels verscheen en het gesprek meteen begon te luisteren.
const WIDGET_SRC = 'https://unpkg.com/@elevenlabs/convai-widget-embed@0.14.9/dist/index.js';

// Vertraging na het load-event als er geen interactie komt. De widget (~420 KB, ~570 ms
// scripting) moet buiten het LCP/TBT-venster blijven; idle alleen was te vroeg.
const AUTO_LOAD_DELAY_MS = 12000;
// Mobiel laadt alleen na echte interactie: de kaart is daar toch verborgen tot de
// bezoeker voorbij de hero scrolt, dus een timer kost alleen LCP/TBT-budget.

// Mobiel: de widget is een vaste kaart onderin die het hero-formulier en de hero-CTA's
// afdekt (390x844). Daarom pas tonen nadat de bezoeker voorbij de hero is gescrold
// (zelfde drempel als de StickyConversionBar) en dan boven die balk plaatsen.
const MOBILE_QUERY = '(max-width: 767px)';
const MOBILE_REVEAL_SCROLL_PX = 900;
const STICKY_BAR_OFFSET_PX = 76;

const LarsVoiceAgent = () => {
  const scriptLoaded = useRef(false);
  const [placement, setPlacement] = useState('hidden');

  useEffect(() => {
    if (typeof window === 'undefined' || isPrerender) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => {
      if (!mq.matches) setPlacement('desktop');
      else setPlacement(window.scrollY > MOBILE_REVEAL_SCROLL_PX ? 'mobile' : 'hidden');
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || isPrerender) return undefined;
    if (scriptLoaded.current || document.querySelector('script[data-convai-loader="1"]')) {
      scriptLoaded.current = true;
      return undefined;
    }

    // Lars laadt vanzelf: de bezoeker hoeft niet eerst te scrollen of te klikken.
    // Wel pas ruim na het load-event, want de widget is een ~590 KB
    // third-party bundle die anders met de hero-render concurreert. Echte interactie
    // (scroll, tap, toets) laadt hem meteen, zonder op de timer te wachten.
    const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
    let timerHandle = null;

    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, load));
      window.removeEventListener('load', schedule);
      if (timerHandle !== null) clearTimeout(timerHandle);
    };

    function load() {
      if (scriptLoaded.current) return;
      scriptLoaded.current = true;
      cleanup();
      const script = document.createElement('script');
      script.src = WIDGET_SRC;
      script.async = true;
      script.type = 'text/javascript';
      script.setAttribute('data-convai-loader', '1');
      document.body.appendChild(script);
    }

    function schedule() {
      if (scriptLoaded.current) return;
      if (window.matchMedia(MOBILE_QUERY).matches) return;
      timerHandle = setTimeout(load, AUTO_LOAD_DELAY_MS);
    }

    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    return cleanup;
  }, []);

  // De "Powered by ElevenLabs" attributie blijft zichtbaar (ToS-vereiste).
  const style =
    placement === 'hidden'
      ? { display: 'none' }
      : placement === 'mobile'
        ? { transform: `translateY(-${STICKY_BAR_OFFSET_PX}px)` }
        : undefined;
  return <elevenlabs-convai agent-id={AGENT_ID} style={style} />;
};

export default LarsVoiceAgent;
